/**
 * Homepage media performance helpers.
 *
 * Native video elements are kept inside <template> until they are roughly one
 * viewport away. This gives the browser enough time to buffer without making
 * every hidden product-card video compete with the hero/LCP content.
 */
function prepareVideoMedia(media) {
  if (!media || media.dataset.videoPrepared === 'true') return;

  const template = media.querySelector('template');
  if (!template) {
    media.dataset.videoPrepared = 'true';
    return;
  }

  const player = template.content.firstElementChild?.cloneNode(true);
  if (!player) return;

  template.replaceWith(player);
  media.dataset.videoPrepared = 'true';

  if (player.tagName === 'VIDEO') {
    player.preload = 'auto';
    player.load();
  }
}

const preparedMedia = new WeakSet();

function initAheadOfScrollVideo(media) {
  if (preparedMedia.has(media)) return;
  preparedMedia.add(media);

  const preloadObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      prepareVideoMedia(entry.target);
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '100% 0px 100% 0px' });

  preloadObserver.observe(media);

  if (media.hasAttribute('data-autoplay-on-scroll')) {
    const playbackObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          prepareVideoMedia(entry.target);
          entry.target.play();
        }
        else {
          entry.target.pause();
        }
      });
    }, { threshold: 0.35 });

    playbackObserver.observe(media);

    const card = media.closest('.product-card');
    if (card) {
      card.addEventListener('mouseenter', () => {
        prepareVideoMedia(media);
        media.play();
      });
      card.addEventListener('mouseleave', () => media.pause());
    }
  }
}

function initOptimizedVideos(root = document) {
  root.querySelectorAll('video-media[autoplay], video-media[data-preload-ahead]')
    .forEach(initAheadOfScrollVideo);
}

document.addEventListener('DOMContentLoaded', () => {
  initOptimizedVideos();

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('video-media[autoplay], video-media[data-preload-ahead]')) {
          initAheadOfScrollVideo(node);
        }
        initOptimizedVideos(node);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
});
